import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../ui/button';
import { 
  LayoutDashboard, 
  Newspaper, 
  FileText, 
  Image, 
  UserCheck,
  Megaphone, 
  Users, 
  CircleAlert, 
  Settings, 
  BarChart3,
  BookOpen,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Newspaper, label: 'News Articles', path: '/admin/articles' },
    { icon: FileText, label: 'E-Paper Upload', path: '/admin/epapers' },
    { icon: BookOpen, label: 'Publications', path: '/admin/publications' },
    { icon: Image, label: 'Photo Gallery', path: '/admin/galleries' },
    { icon: UserCheck, label: 'Citizen Reports', path: '/admin/citizen-reports' },
    { icon: Megaphone, label: 'Advertisements', path: '/admin/advertisements' },
    { icon: Users, label: 'Subscribers', path: '/admin/subscribers' },
    { icon: CircleAlert, label: 'Breaking News', path: '/admin/breaking-news' },
    { icon: Settings, label: 'Website Settings', path: '/admin/settings' },
    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          {sidebarOpen && (
            <h1 className="text-xl font-bold" style={{ fontFamily: 'serif' }}>
              ত্রিপুরা দর্পণ
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-800 rounded"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Admin Info */}
        {sidebarOpen && admin && (
          <div className="p-4 border-b border-gray-800">
            <p className="text-sm text-gray-400">Logged in as</p>
            <p className="font-semibold">{admin.username}</p>
          </div>
        )}

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-200 ${
                  isActive 
                    ? 'bg-red-600 text-white' 
                    : 'hover:bg-gray-800 text-gray-300'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-800">
          <Button
            onClick={handleLogout}
            variant="destructive"
            className="w-full flex items-center justify-center space-x-2"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Admin Panel
            </h2>
            <div className="flex items-center space-x-4">
              <a 
                href="/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-red-600"
              >
                View Website →
              </a>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;